import { router } from "@inertiajs/react"
import { Ellipsis, Loader2 } from "lucide-react"
import { useState } from "react"
import { toast } from "sonner"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { partnerAccountPath } from "@/routes"

// IMP-326: matches the admin portal's tooltip copy for billing-bearing accounts.
const BILLING_RECORDS_REASON =
  "This account has billing records (subscriptions or invoices) and can't be deleted."

interface AccountDeleteMenuProps {
  account: { uid: string; name: string; deletable?: boolean }
}

// "..." dropdown with a Delete option for a partner's account. Disabled with an
// explanatory tooltip when the account has billing records; otherwise opens a
// confirmation dialog that soft-deletes via the partner accounts#destroy route.
// Self-contained (own trigger + confirm dialog) so it drops into both the
// Accounts table rows and the account detail header.
export function AccountDeleteMenu({ account }: AccountDeleteMenuProps) {
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [processing, setProcessing] = useState(false)
  const disabled = account.deletable === false

  const handleDelete = () => {
    setProcessing(true)
    router.delete(partnerAccountPath({ id: account.uid }), {
      onError: () => toast.error("Failed to delete account"),
      onFinish: () => {
        setProcessing(false)
        setConfirmOpen(false)
      },
    })
  }

  const deleteItem = (
    <DropdownMenuItem
      disabled={disabled}
      className="text-red-600"
      onSelect={() => setConfirmOpen(true)}
    >
      Delete account
    </DropdownMenuItem>
  )

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {/* stopPropagation so the menu trigger doesn't fire the table row's
              navigate-to-account click. */}
          <Button
            variant="ghost"
            size="sm"
            className="rounded-sm px-2"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="sr-only">Open menu</span>
            <Ellipsis className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
          {disabled ? (
            <Tooltip>
              <TooltipTrigger asChild>
                {/* Disabled items don't receive pointer events, so the wrapper
                    span anchors the tooltip. */}
                <span className="block">{deleteItem}</span>
              </TooltipTrigger>
              <TooltipContent side="left">
                {BILLING_RECORDS_REASON}
              </TooltipContent>
            </Tooltip>
          ) : (
            deleteItem
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent onClick={(e) => e.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {account.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the account and its data from view. This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive hover:bg-destructive/90 text-white"
              disabled={processing}
            >
              {processing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
